import React, { useState, useEffect, useCallback, useRef } from 'react';
import { Search, Filter, Grid, List } from 'lucide-react';
import { apiService } from '../services/api';
import { useDebounce } from '../hooks/useDebounce';

interface VOD {
  id: string;
  title: string;
  type: 'movie' | 'series' | 'live' | 'radio';
  poster: string;
  year: number;
  rating: number;
  duration: number | null;
  seasons: number | null;
  episodes: number | null;
  category: string;
  package: string;
  description: string;
  added_date: string;
  quality: string;
  size: string | null;
  available: boolean;
}

interface Category {
  id: string;
  name: string;
  count: number;
}

const VODs: React.FC = () => {
  // Estados principais
  const [vods, setVods] = useState<VOD[]>([]);
  const [categories, setCategories] = useState<Category[]>([]);
  const [globalStats, setGlobalStats] = useState<{ total: number; movies: number; series: number; categories: number }>({ total: 0, movies: 0, series: 0, categories: 0 });
  
  // Estados de loading
  const [loading, setLoading] = useState(false);
  
  
  // Estados de UI
  const [viewMode, setViewMode] = useState<'grid' | 'list'>('grid');
  
  // Estados de filtros
  const [filters, setFilters] = useState({
    search: '',
    type: 'all',
    category: '',
    package: '',
    year: '',
    quality: ''
  });
  
  // Debounce para search
  const debouncedSearch = useDebounce(filters.search, 500);
  
  // Estado de paginação
  const [pagination, setPagination] = useState({
    current_page: 1,
    per_page: 20, // Aumentado para 20 para melhor performance
    total: 0,
    total_pages: 0
  });

  // Refs para controle
  const loadingRef = useRef(false);

  // Função para carregar VODs principais
  const loadVODs = useCallback(async () => {
    if (loadingRef.current) return;
    
    try {
      loadingRef.current = true;
      setLoading(true);

      const response = await apiService.getVODs({
        page: pagination.current_page,
        per: pagination.per_page,
        search: debouncedSearch,
        type: filters.type !== 'all' ? filters.type : undefined,
        category: filters.category || undefined
      });

      setVods(response.data || []);
      setPagination(prev => ({
        ...prev,
        total: response.pagination?.total || 0,
        total_pages: response.pagination?.total_pages || 0
      }));

    } catch (error) {
      console.error('🎬 [VODs] Erro ao carregar VODs:', error);
      setVods([]);
    } finally {
      setLoading(false);
      loadingRef.current = false;
    }
  }, [pagination.current_page, pagination.per_page, debouncedSearch, filters.type, filters.category]);



  // Função para carregar categorias
  const loadCategories = useCallback(async () => {
    try {
     
      const response = await apiService.getVODCategories();

      setCategories(response.data || []);
    } catch (error) {
      console.error('🎬 [VODs] Erro ao carregar categorias:', error);
      setCategories([]);
    }
  }, []);

  const loadGlobalStats = useCallback(async () => {
    try {
      const res = await (apiService as any).api.get('/api/vods/stats');
    
      setGlobalStats(res.data?.data || { total: 0, movies: 0, series: 0, categories: 0 });
    } catch (e) {
      setGlobalStats({ total: 0, movies: 0, series: 0, categories: 0 });
    }
  }, []);


  // Effect para carregar dados iniciais (apenas uma vez)
  useEffect(() => {
    const loadInitialData = async () => {
      try {
       
        await Promise.all([
          loadCategories(),
          loadGlobalStats()
        ]);
      
      } catch (error) {
        console.error('🎬 [VODs] Erro ao carregar dados iniciais:', error);
      }
    };

    loadInitialData();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []); // Executar apenas uma vez na montagem

  // Effect para carregar VODs (debounce já aplicado via hook)
  useEffect(() => {
    loadVODs();
  }, [loadVODs]);

  // Função para alterar filtros
  const handleFilterChange = (key: string, value: string) => {
    setFilters(prev => ({ ...prev, [key]: value }));
    setPagination(prev => ({ ...prev, current_page: 1 })); // Reset para primeira página
  };

  // Função para alterar página
  const handlePageChange = (page: number) => {
    setPagination(prev => ({ ...prev, current_page: page }));
  };

  // Função para limpar filtros
  const clearFilters = () => {
    setFilters({
      search: '',
      type: 'all',
      category: '',
      package: '',
      year: '',
      quality: ''
    });
  };

  // Função para formatar duração
  const formatDuration = (seconds: number) => {
    const hours = Math.floor(seconds / 3600);
    const minutes = Math.floor((seconds % 3600) / 60);
    return hours > 0 ? `${hours}h ${minutes}m` : `${minutes}m`;
  };

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex flex-col sm:flex-row justify-between items-start sm:items-center gap-4">
        <div>
          <h1 className="text-2xl font-bold text-gray-900">VODs</h1>
          <p className="text-gray-600">
            {pagination.total.toLocaleString()} filmes e séries disponíveis no sistema
          </p>
        </div>
        
        <div className="flex items-center gap-2">
          <button
            onClick={() => setViewMode('grid')}
            className={`p-2 rounded-lg ${viewMode === 'grid' ? 'bg-blue-500 text-white' : 'bg-gray-100 text-gray-600'}`}
          >
            <Grid className="w-4 h-4" />
          </button>
          <button
            onClick={() => setViewMode('list')}
            className={`p-2 rounded-lg ${viewMode === 'list' ? 'bg-blue-500 text-white' : 'bg-gray-100 text-gray-600'}`}
          >
            <List className="w-4 h-4" />
          </button>
        </div>
      </div>


      {/* Filtros */}
      <div className="bg-white p-6 rounded-lg shadow-sm border">
        <div className="flex items-center justify-between mb-4">
          <h2 className="text-lg font-semibold text-gray-900 flex items-center gap-2">
            <Filter className="w-5 h-5" />
            Filtros de Busca
          </h2>
          <button
            onClick={clearFilters}
            className="text-sm text-blue-600 hover:text-blue-800"
          >
            Limpar Filtros
          </button>
        </div>
        
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
          {/* Busca por nome */}
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">
              Buscar filmes e séries
            </label>
            <div className="relative">
              <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400 w-4 h-4" />
              <input
                type="text"
                placeholder="Digite o nome..."
                value={filters.search}
                onChange={(e) => handleFilterChange('search', e.target.value)}
                className="w-full pl-10 pr-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
              />
            </div>
          </div>

          {/* Tipo de conteúdo */}
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">
              Todos os tipos
            </label>
            <select
              value={filters.type}
              onChange={(e) => handleFilterChange('type', e.target.value)}
              className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
            >
              <option value="all">Todos os tipos</option>
              <option value="movie">Filmes</option>
              <option value="series">Séries</option>
            </select>
          </div>

          {/* Categoria */}
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">
              Todas as categorias
            </label>
            <select
              value={filters.category}
              onChange={(e) => handleFilterChange('category', e.target.value)}
              className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
            >
              <option value="">Todas as categorias</option>
              {categories.map((category) => (
                <option key={category.id} value={category.name}>
                  {category.name} ({category.count})
                </option>
              ))}
            </select>
          </div>

        </div>
      </div>

      {/* Estatísticas */}
      <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
        <div className="bg-white p-4 rounded-lg shadow-sm border text-center">
          <div className="text-2xl font-bold text-blue-600">{(globalStats.total || pagination.total).toLocaleString()}</div>
          <div className="text-sm text-gray-600">Total de VODs</div>
        </div>
        <div className="bg-white p-4 rounded-lg shadow-sm border text-center">
          <div className="text-2xl font-bold text-green-600">{globalStats.movies.toLocaleString()}</div>
          <div className="text-sm text-gray-600">Filmes</div>
        </div>
        <div className="bg-white p-4 rounded-lg shadow-sm border text-center">
          <div className="text-2xl font-bold text-purple-600">{globalStats.series.toLocaleString()}</div>
          <div className="text-sm text-gray-600">Séries</div>
        </div>
        <div className="bg-white p-4 rounded-lg shadow-sm border text-center">
          <div className="text-2xl font-bold text-orange-600">{(globalStats.categories || categories.length).toLocaleString()}</div>
          <div className="text-sm text-gray-600">Categorias</div>
        </div>
      </div>

      {/* Grid/Lista de VODs */}
      <div className="bg-white p-6 rounded-lg shadow-sm border">
        <div className="flex items-center justify-between mb-4">
          <h2 className="text-lg font-semibold text-gray-900">
            VODs Principais ({vods.length} de {pagination.total.toLocaleString()})
          </h2>
          {loading && (
            <div className="flex items-center text-blue-600">
              <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-blue-600 mr-2"></div>
              Carregando...
            </div>
          )}
        </div>

        {loading ? (
          <div className="flex justify-center items-center py-12">
            <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-500"></div>
            <span className="ml-2">Carregando VODs...</span>
          </div>
        ) : vods.length > 0 ? (
          <div className={viewMode === 'grid' 
            ? "grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 lg:grid-cols-5 xl:grid-cols-6 2xl:grid-cols-8 gap-4"
            : "space-y-4"
          }>
            {vods.map((vod) => (
              viewMode === 'grid' ? (
                // Grid View
                <div key={vod.id} className="bg-gray-50 rounded-lg overflow-hidden hover:shadow-md transition-shadow">
                  <div className="relative aspect-[2/3] bg-gray-200">
                    <img
                      src={vod.poster}
                      alt={vod.title}
                      className="w-full h-full object-cover"
                      onError={(e) => {
                        const target = e.target as HTMLImageElement;
                        if (!target.src.includes('placeholder')) {
                          target.src = `https://via.placeholder.com/300x450/6B7280/FFFFFF?text=${encodeURIComponent(vod.title.substring(0, 15))}`;
                        }
                      }}
                    />

                    <div className="absolute top-2 left-2">
                      <span className={`px-2 py-1 text-xs font-medium rounded ${
                        vod.type === 'movie' ? 'bg-blue-500 text-white' : 'bg-purple-500 text-white'
                      }`}>
                        {vod.type === 'movie' ? 'Filme' : 'Série'}
                      </span>
                    </div>

                    <div className="absolute top-2 right-2">
                      <span className="px-2 py-1 text-xs font-medium bg-black bg-opacity-50 text-white rounded">
                        {vod.quality}
                      </span>
                    </div>
                  </div>

                  <div className="p-3">
                    <h3 className="font-medium text-sm mb-1 line-clamp-2" title={vod.title}>
                      {vod.title}
                    </h3>
                    <div className="flex items-center justify-between text-xs text-gray-500 mb-2">
                      <span>{vod.year}</span>
                      <span>⭐ {vod.rating}</span>
                    </div>
                    <div className="text-xs text-gray-600">
                      <div className="truncate">{vod.category}</div>
                      {vod.duration && (
                        <div>{formatDuration(vod.duration)}</div>
                      )}
                    </div>
                  </div>
                </div>
              ) : (
                // List View
                <div key={vod.id} className="flex items-center p-4 border border-gray-200 rounded-lg hover:shadow-md transition-shadow">
                  <div className="w-16 h-24 bg-gray-200 rounded overflow-hidden flex-shrink-0">
                    <img
                      src={vod.poster}
                      alt={vod.title}
                      className="w-full h-full object-cover"
                      onError={(e) => {
                        const target = e.target as HTMLImageElement;
                        if (!target.src.includes('placeholder')) {
                          target.src = `https://via.placeholder.com/64x96/6B7280/FFFFFF?text=${encodeURIComponent(vod.title.substring(0, 3))}`;
                        }
                      }}
                    />
                  </div>
                  
                  <div className="ml-4 flex-grow">
                    <h3 className="font-medium text-gray-900 mb-1">{vod.title}</h3>
                    <div className="flex items-center gap-4 text-sm text-gray-600 mb-2">
                      <span className={`px-2 py-1 text-xs rounded ${
                        vod.type === 'movie' ? 'bg-blue-100 text-blue-800' : 'bg-purple-100 text-purple-800'
                      }`}>
                        {vod.type === 'movie' ? 'Filme' : 'Série'}
                      </span>
                      <span>{vod.year}</span>
                      <span>⭐ {vod.rating}</span>
                      <span>{vod.quality}</span>
                    </div>
                    <div className="text-sm text-gray-600">
                      <div>{vod.category}</div>
                      {vod.duration && <div>{formatDuration(vod.duration)}</div>}
                    </div>
                  </div>
                </div>
              )
            ))}
          </div>
        ) : (
          <div className="text-center py-12">
            <div className="text-gray-500">
              <div className="text-4xl mb-4">🎬</div>
              <p className="text-lg mb-2">Nenhum VOD encontrado</p>
              <p className="text-sm">Verifique os filtros ou tente novamente</p>
            </div>
          </div>
        )}
      </div>

      {/* Paginação */}
      {pagination.total_pages > 1 && (
        <div className="flex justify-center">
          <div className="flex items-center gap-2">
            <button
              onClick={() => handlePageChange(pagination.current_page - 1)}
              disabled={pagination.current_page <= 1}
              className="px-3 py-2 text-sm font-medium text-gray-500 bg-white border border-gray-300 rounded-lg hover:bg-gray-50 disabled:opacity-50 disabled:cursor-not-allowed"
            >
              Anterior
            </button>
            
            <div className="flex items-center gap-1">
              {[...Array(Math.min(5, pagination.total_pages))].map((_, i) => {
                const pageNum = Math.max(1, pagination.current_page - 2) + i;
                if (pageNum > pagination.total_pages) return null;
                
                return (
                  <button
                    key={pageNum}
                    onClick={() => handlePageChange(pageNum)}
                    className={`px-3 py-2 text-sm font-medium rounded-lg ${
                      pagination.current_page === pageNum
                        ? 'bg-blue-500 text-white'
                        : 'text-gray-500 bg-white border border-gray-300 hover:bg-gray-50'
                    }`}
                  >
                    {pageNum}
                  </button>
                );
              })}
            </div>
            
            <button
              onClick={() => handlePageChange(pagination.current_page + 1)}
              disabled={pagination.current_page >= pagination.total_pages}
              className="px-3 py-2 text-sm font-medium text-gray-500 bg-white border border-gray-300 rounded-lg hover:bg-gray-50 disabled:opacity-50 disabled:cursor-not-allowed"
            >
              Próximo
            </button>
          </div>
        </div>
      )}
    </div>
  );
};

export default VODs;